<template>
  <div>
    <main class="offers">
      <card title="Filters">
        <div class="row align-items-end offers-filters__row">
          <div class="col-md-3 pr-md-1">
            <base-input
              v-model="filters.search"
              placeholder="Search by name..."
              @keyup.enter.native="loadOffersFromFilters"
            />
          </div>
          <div class="col-md-2 pr-md-1">
            <el-select
              v-model="filters.type"
              clearable
              class="offers-filters__select"
              placeholder="Type"
              @change="loadOffersFromFilters"
            >
              <el-option label="Procedure" value="procedure" />
              <el-option label="Illness" value="illness" />
            </el-select>
          </div>
          <div class="col-md-2 pr-md-1">
            <el-select
              v-model="filters.is_hide"
              clearable
              class="offers-filters__select"
              placeholder="Visibility"
              @change="loadOffersFromFilters"
            >
              <el-option label="Visible" :value="false" />
              <el-option label="Hidden" :value="true" />
            </el-select>
          </div>
          <div class="col-md-2 pr-md-1">
            <el-select
              v-model="filters.level"
              clearable
              class="offers-filters__select"
              placeholder="Level"
              @change="loadOffersFromFilters"
            >
              <el-option v-for="l in ['1','2','3','4','5']" :key="l" :label="`Level ${l}`" :value="l" />
            </el-select>
          </div>
          <div class="col-md-3 pl-md-1">
            <el-select
              v-model="filters.clinic_id"
              clearable
              filterable
              class="offers-filters__select"
              placeholder="Clinic"
              no-data-text="No data"
              no-match-text="No match"
              @change="loadOffersFromFilters"
            >
              <el-option
                v-for="c in clinics"
                :key="c.id"
                :label="clinicLabel(c)"
                :value="c.id"
              />
            </el-select>
          </div>
        </div>
        <div class="row align-items-end offers-filters__row offers-filters__row--actions mt-3">
          <div class="col-12 d-flex flex-wrap offers-filters__actions">
            <base-button @click="loadOffersFromFilters" class="mr-2">Search</base-button>
            <base-button @click="clearFilters">Clear</base-button>
          </div>
        </div>
      </card>

      <card>
        <div class="offers__title mb-3 d-flex justify-content-between align-items-center flex-wrap">
          <span>Offers ({{ loadedOffersCount }})</span>
          <router-link :to="{ name: 'offers-create' }" class="btn btn-primary offers__create-btn">
            + Create
          </router-link>
        </div>

        <el-table :data="offersList.offers" style="width: 100%">
          <el-table-column prop="id" label="ID" width="230" />
          <el-table-column label="Name" min-width="160">
            <template slot-scope="scope">
              {{ nameEn(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column prop="type" label="Type" width="110" />
          <el-table-column prop="level" label="Level" width="90" align="center" />
          <el-table-column label="Clinic" width="160">
            <template slot-scope="scope">
              {{ scope.row.clinic && scope.row.clinic.name ? scope.row.clinic.name.en || scope.row.clinic.id : '—' }}
            </template>
          </el-table-column>
          <el-table-column label="Hidden" width="86" align="center">
            <template slot-scope="scope">
              {{ scope.row.is_hide ? 'Yes' : 'No' }}
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="200" align="right" fixed="right">
            <template slot-scope="scope">
              <div class="offers-list__actions">
                <button
                  type="button"
                  class="btn btn-primary btn-link"
                  @click="handleEdit(scope.row.id)"
                >
                  <i class="tim-icons icon-pencil"></i>
                </button>
                <button type="button" class="btn btn-danger" @click="handleDelete(scope.row.id)">
                  Delete
                </button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </card>
    </main>

    <el-pagination
      v-if="pageCount > 1"
      background
      prev-text="Previous"
      next-text="Next"
      :page-size="pageSize"
      :pager-count="5"
      layout="prev, pager, next"
      :current-page.sync="page"
      class="text-center mt-20"
      :page-count="pageCount"
      @current-change="loadOffers"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

const PER_PAGE = 50;

export default {
  data: () => ({
    page: 1,
    pageSize: PER_PAGE,
    filters: {
      search: "",
      type: null,
      is_hide: null,
      level: null,
      clinic_id: null
    }
  }),
  computed: {
    ...mapState("offers", ["offersList"]),
    ...mapState("clinics", ["clinics"]),
    pageCount() {
      const meta =
        this.offersList.meta || this.offersList.metadata || {};
      const totalPage = Number(meta.total_page);
      if (totalPage > 0) return totalPage;
      const total = Number(meta.total_count) || 0;
      return total ? Math.ceil(total / this.pageSize) : 0;
    },
    /** Скільки оферів зараз у відповіді (на поточній сторінці списку). */
    loadedOffersCount() {
      const list = this.offersList.offers;
      return Array.isArray(list) ? list.length : 0;
    }
  },
  methods: {
    ...mapActions("offers", ["getOffersList", "deleteOffer"]),
    ...mapActions("clinics", ["getClinics"]),
    clinicLabel(c) {
      if (!c) return "";
      const name = c.name && (c.name.en || c.name.ru);
      return name || String(c.id);
    },
    nameEn(row) {
      if (!row.name) return "—";
      return row.name.en || row.name[Object.keys(row.name)[0]] || "—";
    },
    buildParams() {
      const params = { page: this.page, "per-page": PER_PAGE };
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.type) params.type = this.filters.type;
      if (this.filters.is_hide !== null && this.filters.is_hide !== undefined) {
        params.is_hide = this.filters.is_hide;
      }
      if (this.filters.level) params.level = this.filters.level;
      if (this.filters.clinic_id != null && this.filters.clinic_id !== "") {
        params.clinicIds = [this.filters.clinic_id];
      }
      return params;
    },
    loadOffers() {
      this.$store.dispatch("setLoader", true);
      return this.getOffersList(this.buildParams()).finally(() =>
        this.$store.dispatch("setLoader", false)
      );
    },
    loadOffersFromFilters() {
      this.page = 1;
      this.loadOffers();
    },
    handleEdit(id) {
      this.$router.push({ name: "offers-edit", params: { id } });
    },
    async handleDelete(id) {
      if (!confirm("Delete this offer?")) return;
      this.$store.dispatch("setLoader", true);
      try {
        await this.deleteOffer(id);
        await this.loadOffers();
        this.$notify({ type: "success", title: "Offer deleted" });
      } catch (e) {
        this.$notify({ type: "warning", title: "Error deleting offer" });
      } finally {
        this.$store.dispatch("setLoader", false);
      }
    },
    clearFilters() {
      this.filters = {
        search: "",
        type: null,
        is_hide: null,
        level: null,
        clinic_id: null
      };
      this.page = 1;
      this.loadOffers();
    }
  },
  created() {
    this.getClinics({ perPage: 1500 });
    this.loadOffers();
  }
};
</script>

<style scoped>
.offers-filters__row {
  margin-bottom: 0;
}

.offers-filters__select {
  width: 100%;
}

.offers-filters__row--actions {
  margin-bottom: 0;
}

.offers-filters__actions {
  padding-bottom: 2px;
}

.offers__title {
  gap: 0.75rem;
}

.offers__create-btn {
  flex-shrink: 0;
}

.offers-list__actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  flex-wrap: nowrap;
  white-space: nowrap;
}
</style>
